'use client';

import { useState, type JSX } from 'react';

import clsx from 'clsx';
import type { FieldPath, FieldValues, UseFormRegisterReturn } from 'react-hook-form';

import { inputBackgroundClasses } from '@/components/classes/inputBackgroundClasses';
import { inputBorderClasses } from '@/components/classes/inputBorderClasses';
import { inputPaddingSizeClasses } from '@/components/classes/inputPaddingSizeClasses';
import { placeholderClasses } from '@/components/classes/inputPlaceholderClasses';
import { inputTextSizeClasses } from '@/components/classes/inputTextSizeClasses';
import { textColorClasses } from '@/components/classes/textColorClasses';
import { testIds } from '@/components/constants/testIds';
import { composeEndRadiusClasses } from '@/components/functions/composeEndRadiusClasses';
import { composeRadiusClasses } from '@/components/functions/composeRadiusClasses';
import { composeShadowClasses } from '@/components/functions/composeShowClasses';
import { composeStartRadiusClasses } from '@/components/functions/composeStartRadiusClasses';
import { composeWidthClasses } from '@/components/functions/composeWidthClasses';
import type { Adaptive } from '@/components/types/Adaptive';
import type { AffixBackground } from '@/components/types/AffixBackground';
import type { Color } from '@/components/types/Color';
import type { IconType } from '@/components/types/IconType';
import type { InputBackground } from '@/components/types/InputBackground';
import type { InputBorder } from '@/components/types/InputBorder';
import type { InputTextSize } from '@/components/types/InputTextSize';
import type { PaddingSize } from '@/components/types/PaddingSize';
import type { Radius } from '@/components/types/Radius';
import type { Shadow } from '@/components/types/Shadow';
import type { TextInputAutoComplete } from '@/components/types/TextInputAutoComplete';
import type { TextInputType } from '@/components/types/TextInputType';
import type { Width } from '@/components/types/Width';
import { componentsConfig } from '@/config/componentsConfig';
import appIcons from '@/constants/appIcons';

import Button from '../Button';
import HStack from '../HStack';
import Icon from '../Icon';
import InputAffix from '../InputAffix';
import Spinner from '../Spinner';
import Text from '../Text';

type NewTextInputProps<T extends FieldValues> = {
    type?: TextInputType;
    autoComplete?: TextInputAutoComplete;
    label?: string;
    placeholder?: string;
    prefixText?: string;
    prefixIcon?: IconType;
    prefixBackground?: AffixBackground;
    prefixColor?: Color;
    prefixClassName?: string;
    suffixText?: string;
    suffixIcon?: IconType;
    suffixBackground?: AffixBackground;
    suffixColor?: Color;
    suffixClassName?: string;
    isRequired?: boolean;
    isDisabled?: boolean;
    errorMessage?: string;
    radius?: Adaptive<Radius>;
    width?: Adaptive<Width>;
    textSize?: InputTextSize;
    paddingSize?: PaddingSize;
    shadow?: Adaptive<Shadow>;
    color?: Color;
    background?: InputBackground;
    border?: InputBorder;
    isLoading?: boolean;
    className?: string;
    inputClassName?: string;
    testId?: string;
    register?: UseFormRegisterReturn<FieldPath<T>>;
};

export default function NewTextInput<T extends FieldValues>({
    type = 'text',
    autoComplete,
    label,
    placeholder,
    prefixText,
    prefixIcon,
    prefixClassName,
    prefixColor = componentsConfig.inputAffix.color,
    prefixBackground = componentsConfig.inputAffix.background,
    suffixText,
    suffixIcon,
    suffixClassName,
    suffixColor = componentsConfig.inputAffix.color,
    suffixBackground = componentsConfig.inputAffix.background,
    isRequired,
    isDisabled,
    errorMessage,
    radius = componentsConfig.textInput.radius,
    width = componentsConfig.textInput.width,
    textSize = componentsConfig.textInput.textSize,
    paddingSize = componentsConfig.textInput.paddingSize,
    shadow = componentsConfig.textInput.shadow,
    color = componentsConfig.textInput.color,
    background = componentsConfig.textInput.background,
    border = componentsConfig.textInput.border,
    isLoading = false,
    className,
    inputClassName,
    testId = testIds.textInput.base,
    register,
}: NewTextInputProps<T>): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const [showPassword, setShowPassword] = useState(false);

    const requiredIndicator = isRequired ? '*' : '';

    const basicInputClasses = 'w-full focus:outline-none';
    const disabledClasses = 'opacity-60';
    const secondaryIconClasses = 'flex absolute top-0 h-full end-3 items-center align-middle';

    // ----------------------------------------------------------------------------------------------------
    // MARK: Mini Components
    // ----------------------------------------------------------------------------------------------------
    function inputLoader(): JSX.Element | null {
        if (isLoading) {
            return (
                <div className={clsx(secondaryIconClasses)}>
                    <Spinner size={4} color="tertiary" />
                </div>
            );
        }

        return null;
    }

    function passwordToggler(): JSX.Element | null {
        if (type === 'password' && !isLoading) {
            return (
                <Button
                    variant="link"
                    color={!errorMessage ? 'tertiary' : 'danger'}
                    hoverColor={!errorMessage ? 'tertiary' : 'danger'}
                    className={clsx(secondaryIconClasses)}
                    onClick={() => setShowPassword(!showPassword)}
                >
                    <Icon icon={showPassword ? appIcons.outline.eye : appIcons.outline.eyeSplash} className={suffixClassName} />
                </Button>
            );
        }

        return null;
    }

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <div data-testid={testId} className={clsx('flex flex-col space-y-1', composeWidthClasses(width), className)}>
            {/* Label */}
            {label && (
                <Text testId={testIds.textInput.label} size="xs" weight="medium" color="primary">
                    {label} <span className="text-danger-600">{requiredIndicator}</span>
                </Text>
            )}

            {/* Input */}
            <HStack className={clsx('items-stretch', composeRadiusClasses(radius), composeShadowClasses(shadow), '-space-x-[1px]')}>
                {/* Input Prefix */}
                {(prefixIcon || prefixText) && (
                    <InputAffix
                        direction="start"
                        icon={prefixIcon}
                        text={prefixText}
                        textSize={textSize}
                        radius={radius}
                        border={border}
                        background={!errorMessage ? prefixBackground : 'danger'}
                        color={!errorMessage ? prefixColor : 'danger'}
                        className={prefixClassName}
                    />
                )}

                {/* Input */}
                <div className="relative w-full">
                    <input
                        {...register}
                        data-testid={testIds.textInput.input}
                        type={type === 'password' && showPassword ? 'text' : type}
                        autoComplete={autoComplete ?? 'off'}
                        className={clsx(
                            basicInputClasses,
                            inputTextSizeClasses[textSize],
                            textColorClasses[!errorMessage ? color : 'primary'],
                            inputBorderClasses[!errorMessage ? border : 'danger'],
                            inputBackgroundClasses[!errorMessage ? background : 'transparent'],
                            placeholderClasses[!errorMessage ? background : 'transparent'],
                            inputPaddingSizeClasses[paddingSize],
                            prefixText || prefixIcon ? composeStartRadiusClasses('none') : composeStartRadiusClasses(radius),
                            suffixText || suffixIcon ? composeEndRadiusClasses('none') : composeEndRadiusClasses(radius),
                            !prefixText && !suffixText && !prefixIcon && !suffixIcon && composeRadiusClasses(radius),
                            isDisabled && disabledClasses,
                            inputClassName
                        )}
                        required={isRequired}
                        placeholder={placeholder}
                        disabled={isDisabled}
                    />

                    {/* Input Loader */}
                    {inputLoader()}

                    {/* Show / Hide Password Button */}
                    {passwordToggler()}
                </div>

                {/* Input Suffix */}
                {(suffixIcon || suffixText) && (
                    <InputAffix
                        direction="end"
                        icon={suffixIcon}
                        text={suffixText}
                        textSize={textSize}
                        radius={radius}
                        border={border}
                        background={!errorMessage ? suffixBackground : 'danger'}
                        color={!errorMessage ? suffixColor : 'danger'}
                        className={suffixClassName}
                    />
                )}
            </HStack>

            {/* Error Message */}
            {errorMessage && (
                <Text testId={testIds.textInput.errorMessage} size="2xs" color="danger" className="ms-2">
                    {errorMessage}
                </Text>
            )}
        </div>
    );
}
